import { useEffect, useMemo, useState } from "react";
import { Link, useMatch, useNavigate } from "react-router-dom";
import {
  Bell, BookOpen, ChevronDown, CircleHelp, Globe2, Home, LayoutGrid,
  LogOut, PencilLine, Plus, Search, Settings, Sparkles, UserRound, X,
} from "lucide-react";
import { useAuth } from "../lib/auth";
import SessionUnavailable from "../components/SessionUnavailable";
import { useSelfServiceCreation } from "../features/capabilities/SelfServiceCreation";
import { api, logout, type Alert } from "../lib/api";
import { siteHost } from "../lib/domains";
import { siteDisplayState } from "../lib/siteLifecycle";
import { useDismissable } from "../lib/useDismissable";

type Command = { label: string; hint: string; to: string; icon: typeof Home };

const ACTIVITY_SEEN_KEY = "pp_activity_seen_at";

function relTime(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  const m = Math.round(ms / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.round(h / 24)}d ago`;
}

export function StudioTopbar() {
  const auth = useAuth();
  const showCreation = useSelfServiceCreation();
  const navigate = useNavigate();
  const siteRoute = useMatch("/sites/:siteId/*");
  const [siteOpen, setSiteOpen] = useState(false);
  const [resourcesOpen, setResourcesOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [notifOpen, setNotifOpen] = useState(false);
  const [alerts, setAlerts] = useState<Alert[] | null>(null);
  const [seenAt, setSeenAt] = useState<string>(() => localStorage.getItem(ACTIVITY_SEEN_KEY) ?? "");
  const siteRef = useDismissable<HTMLDivElement>(siteOpen, () => setSiteOpen(false));
  const resourcesRef = useDismissable<HTMLDivElement>(resourcesOpen, () => setResourcesOpen(false));
  const profileRef = useDismissable<HTMLDivElement>(profileOpen, () => setProfileOpen(false));
  const searchRef = useDismissable<HTMLDivElement>(searchOpen, () => setSearchOpen(false));
  const notifRef = useDismissable<HTMLDivElement>(notifOpen, () => setNotifOpen(false));

  // Poll the activity feed while signed in. Cheap read; refreshed on open too.
  useEffect(() => {
    if (auth.status !== "authed") return;
    let alive = true;
    const load = () => api.getActivity().then((r) => { if (alive) setAlerts(r.activity); }).catch(() => { if (alive) setAlerts((a) => a ?? []); });
    void load();
    const timer = window.setInterval(load, 60_000);
    return () => { alive = false; window.clearInterval(timer); };
  }, [auth.status]);

  const unread = alerts ? alerts.filter((a) => a.at > seenAt).length : 0;

  function toggleNotif() {
    setNotifOpen((open) => {
      const next = !open;
      if (next) {
        void api.getActivity().then((r) => setAlerts(r.activity)).catch(() => {});
        if (alerts && alerts[0] && alerts[0].at > seenAt) {
          setSeenAt(alerts[0].at);
          localStorage.setItem(ACTIVITY_SEEN_KEY, alerts[0].at);
        }
      }
      return next;
    });
  }

  const sites = auth.status === "authed" ? (auth.me.sites ?? []).filter(site => !site.archivedAt) : [];
  const commands = useMemo<Command[]>(() => [
    { label: "Home", hint: "Dashboard overview", to: "/dashboard", icon: Home },
    { label: "My sites", hint: "Everything you are building", to: "/sites", icon: LayoutGrid },
    { label: "Web address", hint: "Where each site lives", to: "/domains", icon: Globe2 },
    ...(showCreation ? [{ label: "New website", hint: "Find a business or build from scratch", to: "/start", icon: PencilLine }] : []),
    { label: "Help center", hint: "Guides and answers", to: "/help", icon: BookOpen },
    { label: "What's new", hint: "Latest ProtoPal updates", to: "/whats-new", icon: Sparkles },
    { label: "Settings", hint: "Account and profile", to: "/account", icon: Settings },
    ...sites.map((site) => ({
      label: siteHost(site.subdomain),
      hint: `Open this site · ${siteDisplayState(site)}`,
      to: `/sites/${site.id}`,
      icon: Globe2,
    })),
  ], [sites, showCreation]);
  const matches = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle) return commands.slice(0, 7);
    return commands.filter((command) =>
      command.label.toLowerCase().includes(needle) || command.hint.toLowerCase().includes(needle)).slice(0, 9);
  }, [commands, query]);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "k") {
        event.preventDefault();
        setSearchOpen((value) => !value);
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, []);

  if (auth.status === "error") return <header className="studio-topbar"><SessionUnavailable retry={auth.retry} /></header>;
  if (auth.status !== "authed") return null;
  const currentSite = auth.me.sites?.find((site) => site.id === siteRoute?.params.siteId) ?? sites[0];
  const displayName = auth.me.displayName?.trim() || auth.me.email || "Account";
  const initials = displayName.split(/\s+/).map((part) => part[0]).filter(Boolean).join("").slice(0, 2).toUpperCase();

  const runCommand = (command: Command) => {
    navigate(command.to);
    setSearchOpen(false);
    setQuery("");
  };

  return (
    <header className="studio-topbar">
      <Link to="/dashboard" className="studio-brand" aria-label="ProtoPal Studio home">
        <img src="/favicon.png" alt="" />
        <span>ProtoPal <b>Studio</b></span>
      </Link>
      <div className="studio-menu-wrap" ref={siteRef}>
        <button className="site-switcher" aria-label={`Current site ${currentSite ? siteHost(currentSite.subdomain) : "No site yet"}. Switch site`} onClick={() => setSiteOpen((value) => !value)} aria-expanded={siteOpen} aria-haspopup="menu">
          <span className="site-switcher-thumb"><Globe2 size={15} /></span>
          <span className="site-switcher-copy"><small>Current site</small><strong>{currentSite ? siteHost(currentSite.subdomain) : "No site yet"}</strong></span>
          <ChevronDown size={15} />
        </button>
        {siteOpen && (
          <div className="studio-popover site-popover" role="menu">
            <div className="popover-label">Your sites</div>
            {sites.map((site) => (
              <button key={site.id} role="menuitem" onClick={() => { navigate(`/sites/${site.id}`); setSiteOpen(false); }}>
                <span className="site-switcher-thumb"><Globe2 size={14} /></span>
                <span><strong>{siteHost(site.subdomain)}</strong><small>{siteDisplayState(site)}</small></span>
              </button>
            ))}
            <Link to="/sites" className="popover-action" role="menuitem" onClick={() => setSiteOpen(false)}>Manage workspace &amp; archived sites</Link>
            {showCreation && <Link to="/start" className="popover-action" role="menuitem" onClick={() => setSiteOpen(false)}><Plus size={14} /> New website</Link>}
          </div>
        )}
      </div>
      <div className="topbar-spacer" />
      <div className="topbar-actions">
        <div className="studio-menu-wrap" ref={searchRef}>
          <button className="icon-button search-trigger" aria-label="Search ProtoPal" aria-expanded={searchOpen} onClick={() => setSearchOpen((value) => !value)}>
            <Search size={17} /><span>Search</span><kbd>⌘ K</kbd>
          </button>
          {searchOpen && (
            <div className="studio-popover search-popover" role="dialog" aria-label="Search ProtoPal">
              <div className="command-input">
                <Search size={16} />
                <input
                  autoFocus
                  value={query}
                  placeholder="Search pages and sites…"
                  onChange={(event) => setQuery(event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key === "Enter" && matches[0]) runCommand(matches[0]);
                  }}
                />
                <button onClick={() => { setSearchOpen(false); setQuery(""); }} aria-label="Close search"><X size={15} /></button>
              </div>
              <div className="command-results">
                {matches.map((command) => {
                  const Icon = command.icon;
                  return (
                    <button key={`${command.to}-${command.label}`} onClick={() => runCommand(command)}>
                      <Icon size={15} /><span><strong>{command.label}</strong><small>{command.hint}</small></span>
                    </button>
                  );
                })}
                {matches.length === 0 && <div className="command-empty">Nothing matches “{query.trim()}” yet.</div>}
              </div>
            </div>
          )}
        </div>
        <div className="studio-menu-wrap" ref={resourcesRef}>
          <button className="topbar-link" onClick={() => setResourcesOpen((value) => !value)} aria-expanded={resourcesOpen} aria-haspopup="menu">Resources <ChevronDown size={14} /></button>
          {resourcesOpen && (
            <div className="studio-popover resources-popover" role="menu">
              <Link to="/help" role="menuitem" onClick={() => setResourcesOpen(false)}><CircleHelp size={16} /><span><strong>Help center</strong><small>Guides and answers</small></span></Link>
              <Link to="/whats-new" role="menuitem" onClick={() => setResourcesOpen(false)}><Sparkles size={16} /><span><strong>What&apos;s new</strong><small>Latest ProtoPal updates</small></span></Link>
            </div>
          )}
        </div>
        <Link to="/help" className="icon-button" aria-label="Help"><CircleHelp size={18} /></Link>
        <div className="studio-menu-wrap" ref={notifRef}>
          <button className="icon-button" aria-label="Notifications" aria-expanded={notifOpen} onClick={toggleNotif}>
            <Bell size={18} />
            {unread > 0 && <span className="notification-badge">{unread > 9 ? "9+" : unread}</span>}
          </button>
          {notifOpen && (
            <div className="studio-popover notif-popover" role="menu">
              <div className="popover-label">Notifications</div>
              {alerts === null && <div className="notif-empty">Loading…</div>}
              {alerts && alerts.length === 0 && (
                <div className="notif-empty">No activity yet. Create a site or send it an edit and it&apos;ll show up here.</div>
              )}
              {alerts?.map((a) => {
                const created = a.type === "site_created";
                const Icon = created ? Sparkles : PencilLine;
                return (
                  <button key={a.id} role="menuitem" onClick={() => { navigate(`/sites/${a.siteId}`); setNotifOpen(false); }}>
                    <span className={`notif-icon ${created ? "created" : "edit"}`}><Icon size={14} /></span>
                    <span>
                      <strong>{created ? "New website created" : "Text edit applied"}</strong>
                      <small>{siteHost(a.subdomain)} · {relTime(a.at)}</small>
                    </span>
                  </button>
                );
              })}
            </div>
          )}
        </div>
        <div className="studio-menu-wrap" ref={profileRef}>
          <button className="profile-button" onClick={() => setProfileOpen((value) => !value)} aria-expanded={profileOpen} aria-haspopup="menu">{initials || <UserRound size={16} />}<ChevronDown size={13} /></button>
          {profileOpen && (
            <div className="studio-popover profile-popover" role="menu">
              <div className="profile-summary"><strong>{displayName}</strong><small>{auth.me.email}</small></div>
              <Link to="/account" role="menuitem" onClick={() => setProfileOpen(false)}><Settings size={15} /> Settings</Link>
              <button role="menuitem" onClick={logout}><LogOut size={15} /> Sign out</button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}
